import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { SectionHero, AdminCard, StatusPill, StatGrid, MetricCard } from '@/components/admin/primitives'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Activity, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Database,
  Video,
  Webhook,
  Wrench
} from 'lucide-react'
import { repairMissingPlaybackIds } from '@/lib/video-repair'
import { toast } from 'sonner'

interface HealthCheck {
  name: string
  status: 'checking' | 'success' | 'error' | 'warning'
  message: string
  details?: any
}

export default function SystemHealthDashboard() {
  const [checks, setChecks] = useState<HealthCheck[]>([])
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [isRepairing, setIsRepairing] = useState(false)

  const runHealthChecks = async () => {
    setIsRefreshing(true)
    const newChecks: HealthCheck[] = []

    // Check 1: Database Connection
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('count', { count: 'exact', head: true })
      
      newChecks.push({
        name: 'Database Connection',
        status: error ? 'error' : 'success',
        message: error ? `Failed: ${error.message}` : 'Connected successfully',
        details: { error }
      })
    } catch (e) {
      newChecks.push({
        name: 'Database Connection',
        status: 'error',
        message: 'Exception during connection',
        details: { error: e }
      })
    }

    // Check 2: Videos Table
    try {
      const { data: videos, count, error } = await supabase
        .from('videos')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .limit(10)
      
      newChecks.push({
        name: 'Videos Table',
        status: error ? 'error' : 'success',
        message: error ? `Failed: ${error.message}` : `${count || 0} total videos`,
        details: { count, sample: videos }
      })
    } catch (e) {
      newChecks.push({
        name: 'Videos Table',
        status: 'error',
        message: 'Exception querying videos',
        details: { error: e }
      })
    }

    // Check 3: Missing Playback IDs
    try {
      const { data: videos, error } = await supabase
        .from('videos')
        .select('id, mux_asset_id, mux_playback_id, status')
        .is('mux_playback_id', null)
        .limit(10)
      
      const missingCount = videos?.length || 0
      newChecks.push({
        name: 'Missing Playback IDs',
        status: missingCount > 0 ? 'warning' : 'success',
        message: missingCount > 0 
          ? `${missingCount} videos missing playback IDs` 
          : 'All videos have playback IDs',
        details: { missing: videos }
      })
    } catch (e) {
      newChecks.push({
        name: 'Missing Playback IDs',
        status: 'error',
        message: 'Exception checking playback IDs',
        details: { error: e }
      })
    }

    // Check 4: Telemetry Tables
    try {
      const { count: runsCount } = await supabase
        .from('swing_analysis_runs')
        .select('*', { count: 'exact', head: true })
      
      const { count: blobsCount } = await supabase
        .from('swing_landmark_blobs')
        .select('*', { count: 'exact', head: true })
      
      newChecks.push({
        name: 'Telemetry Tables',
        status: 'success',
        message: `${runsCount || 0} runs, ${blobsCount || 0} blobs`,
        details: { runsCount, blobsCount }
      })
    } catch (e) {
      newChecks.push({
        name: 'Telemetry Tables',
        status: 'error',
        message: 'Exception querying telemetry',
        details: { error: e }
      })
    }

    // Check 5: Auth Status
    try {
      const { data: { session } } = await supabase.auth.getSession()
      newChecks.push({
        name: 'Auth Status',
        status: session ? 'success' : 'warning',
        message: session ? 'Session active' : 'No active session',
        details: { hasSession: !!session }
      })
    } catch (e) {
      newChecks.push({
        name: 'Auth Status',
        status: 'error',
        message: 'Exception checking auth',
        details: { error: e }
      })
    }

    setChecks(newChecks)
    setIsRefreshing(false)
  }

  useEffect(() => {
    runHealthChecks()
    
    if (autoRefresh) {
      const interval = setInterval(runHealthChecks, 30000) // Refresh every 30s
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const handleRepair = async () => {
    setIsRepairing(true)
    try {
      const results = await repairMissingPlaybackIds()
      toast.success(`Repaired ${results.length} videos`)
      runHealthChecks()
    } catch (error) {
      toast.error('Repair failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsRepairing(false)
    }
  }

  const getStatusColor = (status: HealthCheck['status']) => {
    switch (status) {
      case 'success':
        return 'success'
      case 'warning':
        return 'warning'
      case 'error':
        return 'danger'
      default:
        return 'info'
    }
  }

  const healthyCount = checks.filter(c => c.status === 'success').length
  const warningCount = checks.filter(c => c.status === 'warning').length
  const errorCount = checks.filter(c => c.status === 'error').length

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="System Health"
          description="Monitor system status and health checks"
          gradient="success"
          actions={
            <>
              <Button
                onClick={() => setAutoRefresh(!autoRefresh)}
                variant={autoRefresh ? 'default' : 'outline'}
                size="sm"
              >
                <Activity className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-pulse' : ''}`} />
                Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
              </Button>
              <Button
                onClick={runHealthChecks}
                disabled={isRefreshing}
                variant="outline"
                size="sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </>
          }
        />

        {/* Quick Stats */}
        <div className="mt-8">
          <StatGrid columns={3}>
            <MetricCard
              title="Healthy"
              value={healthyCount}
              description={`${checks.length} total checks`}
              size="large"
              accentColor="success"
            />
            <MetricCard
              title="Warnings"
              value={warningCount}
              description="Needs attention"
              size="large"
              accentColor="warning"
            />
            <MetricCard
              title="Errors"
              value={errorCount}
              description="Critical issues"
              size="large"
              accentColor={errorCount > 0 ? 'danger' : 'success'}
            />
          </StatGrid>
        </div>

        {/* Health Checks */}
        <div className="mt-8">
          <Tabs defaultValue="checks" className="space-y-4">
            <TabsList>
              <TabsTrigger value="checks">Health Checks</TabsTrigger>
              <TabsTrigger value="issues">Issues</TabsTrigger>
              <TabsTrigger value="telemetry">Telemetry</TabsTrigger>
              <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
            </TabsList>

            <TabsContent value="checks" className="space-y-4">
              {checks.map((check) => (
                <AdminCard
                  key={check.name}
                  title={check.name}
                  accentColor={getStatusColor(check.status)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm">{check.message}</p>
                      {check.details && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {JSON.stringify(check.details, null, 2)}
                        </p>
                      )}
                    </div>
                    <StatusPill
                      status={
                        check.status === 'success'
                          ? 'healthy'
                          : check.status === 'warning'
                          ? 'degraded'
                          : 'critical'
                      }
                      label={check.status}
                    />
                  </div>
                </AdminCard>
              ))}
            </TabsContent>

            <TabsContent value="issues">
              <AdminCard title="Actionable Issues" accentColor="warning">
                {warningCount + errorCount === 0 ? (
                  <p className="text-muted-foreground">No issues detected</p>
                ) : (
                  <div className="space-y-3">
                    {checks
                      .filter(c => c.status !== 'success')
                      .map((check) => (
                        <Alert key={check.name} variant={check.status === 'error' ? 'destructive' : 'default'}>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            <strong>{check.name}:</strong> {check.message}
                          </AlertDescription>
                        </Alert>
                      ))}
                    {checks.some(c => c.name === 'Missing Playback IDs' && c.status === 'warning') && (
                      <Button
                        onClick={handleRepair}
                        disabled={isRepairing}
                        className="mt-4"
                      >
                        <Wrench className="w-4 h-4 mr-2" />
                        {isRepairing ? 'Repairing...' : 'Repair Videos'}
                      </Button>
                    )}
                  </div>
                )}
              </AdminCard>
            </TabsContent>

            <TabsContent value="telemetry">
              <AdminCard title="Telemetry Stats" accentColor="info">
                <p className="text-muted-foreground">Telemetry statistics coming soon...</p>
              </AdminCard>
            </TabsContent>

            <TabsContent value="webhooks">
              <AdminCard title="Webhook Events" accentColor="info">
                <p className="text-muted-foreground">Webhook event log coming soon...</p>
              </AdminCard>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
